import React, { useMemo } from 'react'

/**
 * CallLogsKpiCards Component
 * Follows the canonical DailyAttendanceMetrics design pattern.
 * Displays 4 KPI metric cards computed from real call logs data with icon badges and skeleton loading.
 */
export default function CallLogsKpiCards({ callLogs = [], loading = false }) {
  const metrics = useMemo(() => {
    const total = callLogs.length
    const inbound = callLogs.filter((l) => l.direction === 'INBOUND').length
    const outbound = callLogs.filter((l) => l.direction === 'OUTBOUND').length

    const totalSecs = callLogs.reduce(
      (acc, l) => acc + (Number(l.duration_seconds) || 0),
      0
    )
    const hrs = Math.floor(totalSecs / 3600)
    const mins = Math.floor((totalSecs % 3600) / 60)
    const talkTimeStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m ${totalSecs % 60}s`

    const avgSecs = total > 0 ? Math.round(totalSecs / total) : 0
    const avgM = Math.floor(avgSecs / 60)
      .toString()
      .padStart(2, '0')
    const avgS = (avgSecs % 60).toString().padStart(2, '0')
    const avgStr = `${avgM}:${avgS}m`

    const completed = callLogs.filter(
      (l) => (l.call_status || '').toUpperCase() === 'COMPLETED'
    ).length
    const missed = callLogs.filter((l) =>
      ['MISSED', 'NO_ANSWER', 'FAILED'].includes(
        (l.call_status || '').toUpperCase()
      )
    ).length
    const successRate = total > 0 ? Math.round((completed / total) * 100) : 100

    return {
      total,
      inbound,
      outbound,
      talkTimeStr,
      avgStr,
      completed,
      missed,
      successRate,
    }
  }, [callLogs])

  const cards = [
    {
      label: 'Total Calls Handled',
      value: loading ? '—' : `${metrics.total}`,
      subtext: `${metrics.inbound} Inbound • ${metrics.outbound} Outbound`,
      icon: 'call',
      color: 'text-primary',
      bg: 'bg-primary/10',
    },
    {
      label: 'Total Talk Time',
      value: loading ? '—' : metrics.talkTimeStr,
      subtext: 'Aggregated voice talk time',
      icon: 'schedule',
      color: 'text-emerald-600',
      bg: 'bg-emerald-500/10',
    },
    {
      label: 'Avg Call Duration',
      value: loading ? '—' : metrics.avgStr,
      subtext: 'Average duration per call',
      icon: 'timer',
      color: 'text-amber-600',
      bg: 'bg-amber-500/10',
    },
    {
      label: 'Call Success Rate',
      value: loading ? '—' : `${metrics.successRate}%`,
      subtext: `${metrics.completed} Completed • ${metrics.missed} Missed`,
      icon: 'check_circle',
      color: 'text-indigo-600',
      bg: 'bg-indigo-500/10',
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-xs"
        >
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${card.bg} ${card.color}`}
          >
            <span className="material-symbols-outlined text-xl">{card.icon}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-black uppercase tracking-wider text-on-surface-variant truncate">
              {card.label}
            </p>
            {loading ? (
              <div className="mt-1.5 h-6 w-20 rounded bg-surface-container-low animate-pulse" />
            ) : (
              <p className="mt-0.5 font-display text-lg font-black tracking-tight text-on-surface truncate">
                {card.value}
              </p>
            )}
            <p className="text-[10px] text-on-surface-variant font-medium truncate mt-0.5">
              {card.subtext}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}
